import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { LoaderCircle } from 'lucide-react';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const buttonStyles = cva(
  'inline-flex items-center justify-center gap-2 rounded-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55 select-none whitespace-nowrap',
  {
    variants: {
      variant: {
        primary: 'bg-pine text-on-pine hover:bg-pine-hover',
        secondary: 'border border-line-strong bg-surface text-ink hover:bg-surface-2',
        ghost: 'text-ink-2 hover:bg-surface-2 hover:text-ink',
        danger: 'border border-rose/40 bg-surface text-rose hover:bg-rose-tint',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-[0.9375rem]',
        lg: 'h-12 px-5 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonStyles({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

const control =
  'w-full rounded-sm border border-line-strong bg-surface px-3 text-[0.9375rem] text-ink placeholder:text-ink-3 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/25 disabled:bg-surface-2 disabled:text-ink-3 aria-[invalid=true]:border-rose';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={cn(control, 'h-10', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cn(control, 'min-h-24 py-2', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cn(control, 'h-10 pe-8', className)} {...rest}>
      {children}
    </select>
  );
});

export interface FieldProps {
  label: string;
  /** Alanın yanındaki yardım simgesi (uygulama tarafından verilir). */
  help?: ReactNode;
  description?: string;
  error?: string;
  required?: boolean;
  optionalLabel?: string;
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode;
  className?: string;
}

/** Etiket + açıklama + hata mesajını erişilebilir biçimde kontrole bağlar. */
export function Field({
  label,
  help,
  description,
  error,
  required,
  optionalLabel,
  children,
  className,
}: FieldProps) {
  const id = useId();
  const descId = description ? `${id}-d` : undefined;
  const errId = error ? `${id}-e` : undefined;
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-medium text-ink">
          {label}
          {!required && optionalLabel ? (
            <span className="ms-1 font-normal text-ink-3">({optionalLabel})</span>
          ) : null}
        </label>
        {help}
      </div>
      {children({
        id,
        'aria-describedby': [descId, errId].filter(Boolean).join(' ') || undefined,
        'aria-invalid': error ? true : undefined,
      })}
      {description && (
        <p id={descId} className="text-[0.8125rem] text-ink-3">
          {description}
        </p>
      )}
      {error && (
        <p id={errId} className="text-[0.8125rem] font-medium text-rose" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export type Tone = 'neutral' | 'pine' | 'moss' | 'amber' | 'rose' | 'slate';

const toneClass: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-2 border-line',
  pine: 'bg-pine-tint text-pine border-pine/20',
  moss: 'bg-moss-tint text-moss border-moss/25',
  amber: 'bg-amber-tint text-amber border-amber/30',
  rose: 'bg-rose-tint text-rose border-rose/30',
  slate: 'bg-slate-tint text-slate border-slate/25',
};

/** Durum rozeti: renk + nokta + metin (renk tek başına bilgi taşımaz). */
export function Badge({
  tone = 'neutral',
  children,
  className,
  dot = true,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.8125rem] font-medium whitespace-nowrap',
        toneClass[tone],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-ink-3" role="status">
      <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

/** Bölüm: başlık + isteğe bağlı açıklama + eylem; kartlar yerine çizgiyle ayrılmış düz bölümler. */
export function Section({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('border-t border-line pt-6', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-[62ch]">
          <h2 className="font-cond text-lg font-semibold text-ink">{title}</h2>
          {description && <div className="mt-0.5 text-sm text-ink-2">{description}</div>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('rounded-md border border-line bg-surface', className)}>{children}</div>;
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-md border border-dashed border-line-strong px-5 py-8 text-center">
      <p className="font-medium text-ink">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-[52ch] text-sm text-ink-2">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function KeyValue({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[minmax(10rem,auto)_1fr]">
      {items.map((i) => (
        <div key={i.label} className="contents">
          <dt className="text-sm text-ink-3">{i.label}</dt>
          <dd className="text-[0.9375rem] text-ink break-words">{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}
